










































































import { Component, Vue, Prop, Watch } from "vue-property-decorator";
// utilities
import { Chart } from "highcharts-vue";
import Highcharts from "highcharts";
import moment from "moment";
import dateHelper from "@/Scripts/utilities/date-helper";

// types
import { IDataSerie } from "@/types/highcharts/dataSerie";
import { DataQualityFeatureAlignment } from "@/types/dataQuality/dataAlignment";

@Component({
  components: {
    Highcharts: Chart,
  },
})
export default class DataAlignmentChart extends Vue {
  @Prop({ required: true }) alignmentData!: DataQualityFeatureAlignment;
  @Prop({ required: true }) dateRange!: { from: string; to: string };
  @Prop() loading!: boolean;

  chart: any = null;
  chartLoaded = false;

  get ChartOptions(): any {
    if (!this.chartLoaded || !Highcharts) return {};
    const ctx = this;
    return {
      chart: ctx.chartSettings,
      title: { text: "" },
      legend: {
        enabled: false,
        align: "right",
        verticalAlign: "top",
        x: 0,
        y: -30,
        itemStyle: {
          color: "#666666",
          cursor: "pointer",
          fontSize: "14px",
          fontWeight: 400,
        },
      },
      yAxis: {
        min: 0,
        max: null,
        title: { text: "Values" },
        labels: {
          format: "{value}",
          style: { color: this.themeColor("ContentPrimary") },
        },
        gridLineColor: this.themeColor("ContentInverseSecondary"),
      },
      xAxis: {
        title: { text: null },
        type: "datetime",
        minRange: 1,
        labels: { y: 35, style: { color: this.themeColor("ContentPrimary") } },
        minorTickLength: 0,
        tickLength: 4,
        min: Date.parse(this.dateRange.from),
        max: Date.parse(this.dateRange.to),
        lineColor: this.themeColor("ContentInverseSecondary"),
      },
      plotOptions: {
        area: {
          threshold: 0,
          marker: { enabled: false },
        },
        series: {
          showInLegend: true,
          dataLabels: { enabled: false },
        },
      },
      tooltip: {
        useHTML: true,
        backgroundColor: "#000",
        borderWidth: 0,
        borderRadius: 13,
        shadow: false,
        style: {
          color: "#fff",
          fontSize: "14px",
        },
        formatter: function () {
          const $this: any = this;
          return `
                <div style="line-height: 18px;">
                  <span style="margin-bottom: 5px; font-size: 12px;">${dateHelper.getFormatedDateString($this.point.x)}</span> <br />
                  <span style="font-size: 12px;">${Number($this.point.y.toFixed(4))}</span>
                </div>
              `;
        },
      },
      series: undefined,
      credits: { enabled: false },
      exporting: { enabled: false },
    };
  }

  get chartSettings(): any {
    const ctx = this;
    return {
      type: "line",
      zoomType: "x",
      spacingRight: 0,
      spacingLeft: 0,
      spacingTop: 28,
      height: 315,
      backgroundColor: this.themeColor("BackgroundPrimaryDefault"),
      style: { fontFamily: "Helvetica Neue" },
      events: {
        selection: function (e: any) {
          const $this: any = this;
          setTimeout(() => {
            ctx.$emit("onZoom", {
              from: $this.xAxis[0].min,
              to: $this.xAxis[0].max,
              resetZoom: Boolean(e.resetSelection),
            });
            return false;
          }, 100);
        },
      },
    };
  }

  get noData() {
    return !this.alignmentData?.featureValues?.length;
  }

  chartReady(chart: any): void {
    this.chart = chart;
    this.chartLoaded = true;
    this.updateChart(); // kall første gang når klart
  }

  @Watch("alignmentData", { immediate: true, deep: true })
  onAlignmentDataChange(): void {
    if (this.chartLoaded && this.chart) {
      this.updateChart();
    }
  }

  updateChart(): void {
    // if (!this.chart || !this.alignmentData || !this.chart.series || !this.chart.series.length) return;

    // console.log("series", this.chart.series);

    // while (this.chart.series.length > 0) {
    //   this.chart.series[0].remove(false);
    // }
    if (!this.alignmentData) return;

    if (this.chart && this.chart.series && this.chart.series.length) {
      /* IMPORTANT: There is also a bug in Highcharts when it has more than 2 series and they are updated dynamically it "loses" correct indexes of the series and displays data in a weird manner so the solution is to clear the series array before it will be updated */
      while (this.chart.series.length) {
        this.chart.series[0].remove();
      }
    }

    const newSeries = this.buildSeries();
    const newNoonSeries = this.buildNoonSeries();
    this.chart.addSeries(newSeries, false);
    this.chart.addSeries(newNoonSeries, true);
    this.chart.redraw();
  }

  buildSeries(): any {
    return {
      name: this.alignmentData.featureName,
      type: "line",
      data: (this.alignmentData?.featureValues ?? [])
        .filter(v => v.value !== null && v.value !== undefined && !!v.time)
        .map(v => {
          const ms = moment(v.time).isValid() ? moment(v.time).valueOf() : null;
          if (!ms) console.warn("Invalid timestamp:", v.time);
          return [ms, v.value];
        }),
      color: this.themeColor("BackgroundBrandSecondary"),
      lineWidth: 2,
      marker: {
        enabled: false,
        symbol: "circle",
        radius: 3,
      },
    };
  }

  buildNoonSeries(): any {
    return {
      name: this.alignmentData.noonFeatureName,
      type: "line",
      data: (this.alignmentData?.noonFeatureValues ?? [])
        .filter(v => v.value !== null && v.value !== undefined && !!v.time)
        .map(v => {
          const ms = moment(v.time).isValid() ? moment(v.time).valueOf() : null;
          if (!ms) console.warn("Invalid timestamp:", v.time);
          return [ms, v.value];
        }),
      color: "#085A8B",
      lineWidth: 2,
      marker: {
        enabled: false,
        symbol: "circle",
        radius: 3,
      },
    };
  }

  zoom(selection: { from: string; to: string; resetZoom: boolean }): void {
    if (this.chart) {
      this.chart.xAxis[0].zoom(selection.from, selection.to);
      this.chart.redraw();
    }
  }
  themeColor(color: string): string {
    return (this.$vuetify.theme.themes[this.$vuetify.theme.dark ? "dark" : "light"][color] as any) ?? this.$vuetify.theme.themes.light[color];
  }
}
